import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import { useBehaviorTracker } from 'contexts/behaviors';
import Duplicate, { duplicateAction } from 'components/ContextMenu/actions/Duplicate';
import Delete, { deleteAction } from 'components/ContextMenu/actions/Delete';
import Rotate, { rotateAction } from 'components/ContextMenu/actions/Rotate';
import ContextButton from 'components/ContextMenu/ContextButton';
import { useSpring } from 'react-spring';
import { animated } from '@react-spring/web';
import { useSelected } from 'contexts/selected';
import { useIntl } from 'react-intl';
import { getSelectedGroupId } from 'ducks/products';
import { RotatableComponent } from '@inter-ikea-storageone/range';
import NonDuplicatableComponent from '@inter-ikea-storageone/range/src/core-api/components/NonDuplicatableComponent';

const Wrapper = styled(animated.div)`
  position: absolute;
  bottom: ${({ theme }) => theme.space.xs};
  display: flex;
  flex-direction: row;
  justify-content: center;
  background: ${props => props.theme.colors.feedback.selected};
  border-radius: ${({ theme }) => theme.space.mdl};
`;

const Divider = styled.div`
  width: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.white};
  height: 24px;
  margin: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.xxs};
`;

const ContextMenu = () => {
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const { selectedMenu } = useSelected();
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const nonDuplicateable = selectedEntity?.hasComponent(NonDuplicatableComponent.id);
  const enableDuplicate = !nonDuplicateable;
  const rotateEnabled = selectedEntity?.hasComponent(RotatableComponent.id);

  const wideMenu = selectedEntity && enableDuplicate;
  const { formatMessage } = useIntl();

  const DuplicateButton = () => {
    const onDuplicate = () => {
      selectedEntity && duplicateAction(selectedEntity, coreApi, formatMessage);
    };

    return enableDuplicate ? (
      <ContextButton onClick={onDuplicate}>
        {selectedEntity && <Duplicate selectedEntity={selectedEntity} size={'xsmall'} />}
      </ContextButton>
    ) : null;
  };

  const RotateButton = () => {
    const onRotate = () => {
      selectedEntity && rotateAction(coreApi, selectedEntity);
    };

    return rotateEnabled ? (
      <>
        <Divider />
        <ContextButton onClick={onRotate}>
          {selectedEntity && <Rotate selectedEntity={selectedEntity} size={'xsmall'} />}
        </ContextButton>
      </>
    ) : null;
  };

  const DeleteButton = () => {
    const selectedGroupId = useSelector(getSelectedGroupId);

    const onDelete = () => {
      selectedEntity &&
        deleteAction(
          selectedEntity,
          selectedGroupId,
          coreApi,
          behaviorTracker,
          selectedMenu?.behaviors
        );
    };

    // This is needed for the componnent to render with the correct groupId
    useEffect(() => {}, [selectedGroupId]);

    return selectedEntity ? (
      <ContextButton onClick={onDelete}>
        {selectedEntity && <Delete selectedEntity={selectedEntity} size={'xsmall'} />}
      </ContextButton>
    ) : null;
  };

  const animation = useSpring({
    opacity: selectedEntity ? 1 : 0,
    width: 'auto',
    zIndex: selectedEntity ? 1 : -1
  });

  return (
    <Wrapper
      style={{
        opacity: animation.opacity,
        width: animation.width,
        zIndex: animation.zIndex
      }}
    >
      <DuplicateButton />
      {wideMenu && <Divider />}
      <DeleteButton />
      <RotateButton />
    </Wrapper>
  );
};

export default ContextMenu;
